import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaSms, FaWhatsapp, FaSave, FaSyncAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";

/* ------------------------------------------------------------------ */
/* Constants & helpers                                                */
/* ------------------------------------------------------------------ */

const VARIABLE_OPTIONS = [
  { key: "lead.name", label: "Lead name" },
  { key: "executive.name", label: "Executive name" },
  { key: "videocall.link", label: "Video call link" },
  { key: "videocall.hostLink", label: "Host link" },
  { key: "videocall.password", label: "Video call password" },
];

// Matches {{1}}, {{var1}}, {{name}} and DLT style {#var#}
const PLACEHOLDER_REGEX = /\{\{[^}]+\}\}|\{#var#\}/g;

const getPlaceholders = (text) => (text ? text.match(PLACEHOLDER_REGEX) || [] : []);
const getUniquePlaceholders = (text) => [...new Set(getPlaceholders(text))];

const inputClass =
  "w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]";

const labelClass = "block text-xs font-medium mb-1 dark:text-gray-300";

/* ------------------------------------------------------------------ */
/* Small UI pieces                                                    */
/* ------------------------------------------------------------------ */

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none flex-shrink-0">
    <span className="text-xs text-gray-600 dark:text-gray-300">
      {checked ? "Enabled" : "Disabled"}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative w-8 h-4 rounded-full transition-colors flex-shrink-0 ${
        checked ? "bg-[#673AB7]" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`absolute top-[2px] left-[2px] w-3 h-3 bg-white rounded-full transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </button>
  </label>
);

const MappingList = ({ placeholders, mapping, onChange }) => {
  if (placeholders.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        This template has no placeholders, so there is nothing to map.
      </p>
    );
  }

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-100 dark:border-gray-700 mt-4">
      <h4 className="cfg-heading text-xs font-semibold mb-3 dark:text-gray-300">
        Map placeholders
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {placeholders.map((ph, idx) => (
          <div key={`${ph}-${idx}`}>
            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1">
              <span className="font-mono bg-white dark:bg-gray-800 px-1 py-0.5 rounded border border-gray-200 dark:border-gray-600 text-[#673AB7] dark:text-[#8b5cf6]">
                {ph}
              </span>
              <span className="text-gray-400 ml-1">(position {idx + 1})</span>
            </label>
            <select
              value={mapping[idx] || ""}
              onChange={(e) => onChange(idx, e.target.value)}
              className={inputClass}
            >
              <option value="">Select value</option>
              {VARIABLE_OPTIONS.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.label} ({v.key})
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

const Preview = ({ text, placeholders, mapping }) => {
  const rendered = useMemo(() => {
    let out = text || "";
    if (placeholders.length === 0) return out;

    let seq = 0;
    out = out.replace(PLACEHOLDER_REGEX, (match) => {
      const idx = placeholders.indexOf(match);
      const pos = match === "{#var#}" ? seq : idx;
      seq += 1;
      const key = mapping[pos];
      return key ? `[${key}]` : match;
    });
    return out;
  }, [text, placeholders, mapping]);

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-100 dark:border-gray-700 mt-4">
      <h4 className="cfg-heading text-xs font-semibold mb-2 dark:text-gray-300">Preview</h4>
      <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg min-h-[64px] text-xs whitespace-pre-wrap text-gray-800 dark:text-gray-200">
        {rendered || (
          <span className="text-gray-400 italic">Select a template to see its preview.</span>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */

const Videocallconfig = () => {
  const { user } = useContext(AuthContext);
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
  const token = user?.token || localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);

  // Added state for handling tabs (SMS vs WhatsApp)
  const [activeTab, setActiveTab] = useState("sms");

  const [smsTemplates, setSmsTemplates] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  const [config, setConfig] = useState({
    smsEnabled: false,
    smsTemplateId: "",
    smsVariableMapping: [],
    whatsappEnabled: false,
    whatsappTemplateId: "",
    whatsappVariableMapping: [],
  });
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  /* ---------------------------- Fetchers --------------------------- */

  const fetchSmsTemplates = async () => {
    const res = await axios.get(`${API_URL}/api/smsTemplates/company/${companyId}`, {
      headers,
    });
    const list = res.data?.smsTemplates || [];
    return list.map((t, index) => ({
      // FIX: Added 't.messageId' to correctly capture the Mongo ObjectId instead of falling back to 'sms_0'
      // Pehle yahan 'mesaageId' (typo) tha jiski wajah se ye fail ho raha tha
      id: String(t.messageId || t._id || t.id || t.mesaageId || `sms_${index}`),
      senderId: t.senderId || "Unknown",
      dltTemplateId: t.templateId || "N/A",
      type: t.templateType || "Standard",
      text: t.templateText || t.message || "",
    }));
  };

  const fetchWhatsappTemplates = async () => {
    // Calls the single backend proxy endpoint using API_URL
    const res = await axios.get(`${API_URL}/api/whatsappConfig/templates/${companyId}`, {
      headers,
    });

    if (res.data?.status !== "success" || !Array.isArray(res.data?.data)) {
      throw new Error(res.data?.message || "Failed to load WhatsApp templates");
    }

    return res.data.data.map((t, index) => {
      // Find the body text regardless of how the provider structures it
      const body = (t.components || []).find((c) => c.type === "BODY");
      const templateText = body?.text || t.text || t.body || "";
      
      // CRITICAL: Use the template's name as the unique identifier so it gets passed to /save as whatsappTemplateId
      const templateName = t.name || t.templateName || `Unnamed_${index}`;
      
      const isApproved =
        t.status === "APPROVED" ||
        t.status === "approved" ||
        t.active === true ||
        t.status === undefined;

      return {
        id: templateName, // This ensures config.whatsappTemplateId stores the template NAME
        name: templateName,
        category: t.category ? t.category.toLowerCase() : "uncategorized",
        language: t.language || "en_US",
        status: isApproved ? "approved" : "pending",
        text: templateText,
      };
    });
  };

  const fetchSavedConfig = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/videocalls/template-config/${companyId}`,
        { headers }
      );
      if (res.data?.status === "success" && res.data.data) {
        return res.data.data;
      }
      return null;
    } catch (err) {
      if (err.response?.data?.status === "error" || err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  };

  const loadAll = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setTemplatesError(null);

    const [smsResult, waResult, savedResult] = await Promise.allSettled([
      fetchSmsTemplates(),
      fetchWhatsappTemplates(),
      fetchSavedConfig(),
    ]);

    const errors = [];

    if (smsResult.status === "fulfilled") setSmsTemplates(smsResult.value);
    else errors.push("SMS templates could not be loaded.");

    if (waResult.status === "fulfilled") {
      setWhatsappTemplates(waResult.value);
    } else {
      errors.push(
        `WhatsApp templates could not be loaded: ${
          waResult.reason?.response?.data?.message || waResult.reason?.message || "unknown error"
        }`
      );
    }

    if (savedResult.status === "fulfilled") {
      const saved = savedResult.value;
      if (saved) {
        setHasExistingConfig(true);
        setConfig({
          smsEnabled: !!saved.smsEnabled,
          smsTemplateId: saved.smsTemplateId ? String(saved.smsTemplateId) : "",
          smsVariableMapping: saved.smsVariableMapping || [],
          whatsappEnabled: !!saved.whatsappEnabled,
          whatsappTemplateId: saved.whatsappTemplateId ? String(saved.whatsappTemplateId) : "",
          whatsappVariableMapping: saved.whatsappVariableMapping || [],
        });
      }
    } else {
      errors.push("Saved configuration could not be loaded.");
    }

    if (errors.length) setTemplatesError(errors.join(" "));
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /* ------------------------- Derived values ------------------------ */

  const selectedSms = smsTemplates.find((t) => String(t.id) === String(config.smsTemplateId));
  const selectedWa = whatsappTemplates.find((t) => String(t.id) === String(config.whatsappTemplateId));

  const smsPlaceholders = useMemo(() => {
    const all = getPlaceholders(selectedSms?.text);
    const hasDlt = all.some((p) => p === "{#var#}");
    return hasDlt ? all : getUniquePlaceholders(selectedSms?.text);
  }, [selectedSms]);

  const waPlaceholders = useMemo(() => getUniquePlaceholders(selectedWa?.text), [selectedWa]);

  /* --------------------------- Handlers ---------------------------- */

  const update = (patch) => setConfig((prev) => ({ ...prev, ...patch }));

  const handleTemplateChange = (channel, templateId) => {
    if (channel === "sms") {
      update({ smsTemplateId: String(templateId), smsVariableMapping: [] });
    } else {
      // templateId here is actually the template name based on our mapping in fetchWhatsappTemplates
      update({ whatsappTemplateId: String(templateId), whatsappVariableMapping: [] });
    }
  };

  const handleMappingChange = (channel, idx, value) => {
    const field = channel === "sms" ? "smsVariableMapping" : "whatsappVariableMapping";
    const count = channel === "sms" ? smsPlaceholders.length : waPlaceholders.length;
    const next = Array.from({ length: count }, (_, i) => config[field][i] || "");
    next[idx] = value;
    update({ [field]: next });
  };

  const validate = () => {
    if (!config.smsEnabled && !config.whatsappEnabled) {
      toast.error("Enable at least one channel (SMS or WhatsApp).");
      return false;
    }

    if (config.smsEnabled) {
      if (!config.smsTemplateId) {
        setActiveTab("sms");
        toast.error("Select an SMS template.");
        return false;
      }
      const missing = smsPlaceholders.some((_, i) => !config.smsVariableMapping[i]);
      if (missing) {
        setActiveTab("sms");
        toast.error("Map every SMS placeholder to a value.");
        return false;
      }
    }

    if (config.whatsappEnabled) {
      if (!config.whatsappTemplateId) {
        setActiveTab("whatsapp");
        toast.error("Select a WhatsApp template.");
        return false;
      }
      const missing = waPlaceholders.some((_, i) => !config.whatsappVariableMapping[i]);
      if (missing) {
        setActiveTab("whatsapp");
        toast.error("Map every WhatsApp placeholder to a value.");
        return false;
      }
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // The whatsappTemplateId is the template name since we mapped id -> t.name above
    const payload = {
      companyId,
      smsEnabled: config.smsEnabled,
      smsTemplateId: config.smsEnabled ? config.smsTemplateId : "",
      smsVariableMapping: config.smsEnabled
        ? smsPlaceholders.map((_, i) => config.smsVariableMapping[i])
        : [],
      whatsappEnabled: config.whatsappEnabled,
      whatsappTemplateId: config.whatsappEnabled ? config.whatsappTemplateId : "",
      whatsappVariableMapping: config.whatsappEnabled
        ? waPlaceholders.map((_, i) => config.whatsappVariableMapping[i])
        : [],
    };

    try {
      setSaving(true);
      const res = await axios.post(
        `${API_URL}/api/videocalls/template-config/save`,
        payload,
        { headers }
      );

      if (res.data?.status === "success") {
        toast.success(
          hasExistingConfig ? "Configuration updated successfully!" : "Configuration saved successfully!"
        );
        setHasExistingConfig(true);
      } else {
        toast.error(res.data?.message || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Save config error:", err);
      toast.error(err.response?.data?.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------- Render ---------------------------- */

 if (loading) {
    return (
      <div className="mx-auto bg-[#FAF8FF] dark:bg-gray-900 p-5 rounded-xl shadow-sm transition-all duration-200 min-h-screen cfg-font flex justify-center items-center">
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
        <div className="text-xs text-gray-500 dark:text-gray-400">Loading configuration...</div>
      </div>
    );
  }

if (!companyId) {
    return (
      <div className="mx-auto bg-[#FAF8FF] dark:bg-gray-900 p-5 rounded-xl shadow-sm transition-all duration-200 min-h-screen cfg-font">
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
        <div className="max-w-3xl mt-8 p-6 rounded bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400">
          Company ID is missing. Log in again to configure video call messages.
        </div>
      </div>
    );
  }

 return (
    <div className="mx-auto bg-[#FAF8FF] dark:bg-gray-900 p-5 rounded-xl shadow-sm transition-all duration-200 min-h-screen cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-5 gap-4">
        <div>
          <h2 className="text-xl font-bold bg-gradient-to-r from-[#8B5CF6] to-[#673AB7] bg-clip-text text-transparent cfg-heading">
            Video Call Configuration
          </h2>
          <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
            Choose the templates used to send video call links to leads.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`px-2 py-1 rounded text-[10px] uppercase font-semibold tracking-wide ${
              hasExistingConfig
                ? "bg-[#F3E8FF] text-[#673AB7] dark:bg-purple-900/30 dark:text-purple-300"
                : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {hasExistingConfig ? "Configured" : "Not configured"}
          </span>
          <button
            type="button"
            onClick={loadAll}
            className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 dark:text-white rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
          >
            <FaSyncAlt size={10} /> Refresh
          </button>
        </div>
      </div>

      {templatesError && (
        <div
          className="max-w-3xl bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-xs dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
          role="alert"
        >
          {templatesError}
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200 dark:border-gray-700 max-w-3xl">
        <button
          onClick={() => setActiveTab("sms")}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-t-lg transition-colors ${
            activeTab === "sms"
              ? "text-[#673AB7] dark:text-[#8B5CF6] border-b-2 border-[#673AB7] dark:border-[#8B5CF6] bg-white dark:bg-gray-800"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          <FaSms size={13} /> SMS Configuration
        </button>
        <button
          onClick={() => setActiveTab("whatsapp")}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-t-lg transition-colors ${
            activeTab === "whatsapp"
              ? "text-[#673AB7] dark:text-[#8B5CF6] border-b-2 border-[#673AB7] dark:border-[#8B5CF6] bg-white dark:bg-gray-800"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          <FaWhatsapp size={13} /> WhatsApp Configuration
        </button>
      </div>

      {/* Form Container */}
      <form onSubmit={handleSave} className="max-w-3xl space-y-5">
        
        {/* ================= SMS TAB ================= */}
        {activeTab === "sms" && (
          <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white">
                  SMS Channel
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Send the video call link by text message.
                </p>
              </div>
              <Toggle
                checked={config.smsEnabled}
                onChange={(v) => update({ smsEnabled: v })}
                label="Enable SMS"
              />
            </div>

            <div className={`transition-opacity ${config.smsEnabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
              <div>
                <label className={labelClass}>SMS template</label>
                <select
                  value={config.smsTemplateId || ""}
                  onChange={(e) => handleTemplateChange("sms", e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select an SMS template</option>
                  {smsTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.senderId} - {t.dltTemplateId} ({t.type})
                    </option>
                  ))}
                </select>
                {smsTemplates.length === 0 && (
                  <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                    No SMS templates found. Add one on the SMS Templates page first.
                  </p>
                )}
              </div>

              {selectedSms && (
                <>
                  <MappingList
                    placeholders={smsPlaceholders}
                    mapping={config.smsVariableMapping}
                    onChange={(idx, val) => handleMappingChange("sms", idx, val)}
                  />
                  <Preview
                    text={selectedSms.text}
                    placeholders={smsPlaceholders}
                    mapping={config.smsVariableMapping}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* ================= WHATSAPP TAB ================= */}
        {activeTab === "whatsapp" && (
          <div className="p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h3 className="cfg-heading text-sm font-semibold text-gray-800 dark:text-white">
                  WhatsApp Channel
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Send the video call link on WhatsApp.
                </p>
              </div>
              <Toggle
                checked={config.whatsappEnabled}
                onChange={(v) => update({ whatsappEnabled: v })}
                label="Enable WhatsApp"
              />
            </div>

            <div className={`transition-opacity ${config.whatsappEnabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
              <div>
                <label className={labelClass}>WhatsApp template</label>
                <select
                  value={config.whatsappTemplateId || ""}
                  onChange={(e) => handleTemplateChange("whatsapp", e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select a WhatsApp template</option>
                  {whatsappTemplates.map((t) => (
                    <option key={t.id} value={t.id} disabled={t.status !== "approved"}>
                      {t.name} ({t.category}, {t.language})
                      {t.status !== "approved" ? " - pending approval" : ""}
                    </option>
                  ))}
                </select>
                {whatsappTemplates.length === 0 && (
                  <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                    No WhatsApp templates found. Check the WhatsApp configuration for this company.
                  </p>
                )}
              </div>

              {selectedWa && (
                <>
                  <MappingList
                    placeholders={waPlaceholders}
                    mapping={config.whatsappVariableMapping}
                    onChange={(idx, val) => handleMappingChange("whatsapp", idx, val)}
                  />
                  <Preview
                    text={selectedWa.text}
                    placeholders={waPlaceholders}
                    mapping={config.whatsappVariableMapping}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-5 py-2 text-xs font-medium text-white bg-[#673AB7] rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors shadow-sm"
          >
            <FaSave className="mr-2" />
            {saving ? "Saving..." : hasExistingConfig ? "Update Configuration" : "Save Configuration"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Videocallconfig;